import React, { useEffect, useMemo, useRef, useState } from 'react';
import { zonedDayKey } from '../utils/kernelTimezone';
import { NT_COLORS } from './NotionUI';
import { useLanguage } from '../i18n/LanguageContext';

const DAY_MS = 86400000;
const ZOOMS = { day: 32, week: 14, month: 5, quarter: 2 };
const ROW_HEIGHT = 36;
const MIN_DAYS = 28;

// Le date sono stringhe "AAAA-MM-GG": si lavora su numeri di giorno UTC per evitare problemi di fuso orario.
const toDay = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
};
const toIso = (day) => new Date(day * DAY_MS).toISOString().slice(0, 10);
const todayDay = () => {
  const [y, m, d] = zonedDayKey(new Date()).split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
};

/**
 * Vista Gantt: ogni attività con una data di inizio (e, se c'è, di fine) è una
 * barra sulla linea del tempo. Si trascina la barra per spostarla e i bordi
 * per cambiarne la durata; un clic apre la scheda dell'attività.
 */
function ProjectGantt({ tasks, startColumn, endColumn, dependencyColumn, statusColumn, color, zoom, onOpen, onShift }) {
  const { t, lang } = useLanguage();
  const dayWidth = ZOOMS[zoom];
  const scrollRef = useRef(null);
  const [drag, setDrag] = useState(null); // anteprima durante il trascinamento: {id, start, end}
  const dragRef = useRef(null);

  const startKey = String(startColumn.id);
  const endKey = endColumn ? String(endColumn.id) : null;

  // Attività con date: intervallo [start, end] in giorni (end mai prima di start).
  const isRange = startColumn.type === 'daterange';
  const readRange = (task) => {
    const raw = task.data[startKey];
    if (isRange) {
      if (!raw || (!raw.start && !raw.end)) return null;
      const start = toDay(raw.start || raw.end);
      return { start, end: Math.max(start, toDay(raw.end || raw.start)), hasEnd: Boolean(raw.end) };
    }
    if (!raw) return null;
    const start = toDay(raw);
    const rawEnd = endKey && task.data[endKey] ? toDay(task.data[endKey]) : start;
    return { start, end: Math.max(start, rawEnd), hasEnd: Boolean(endKey && task.data[endKey]) };
  };
  const scheduled = useMemo(() => tasks
    .map((task) => ({ task, ...(readRange(task) || {}) }))
    .filter((item) => item.start !== undefined),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [tasks, startKey, endKey, isRange]);
  const unscheduled = useMemo(() => tasks.filter((task) => !scheduled.some((item) => item.task.id === task.id)), [tasks, scheduled]);

  const today = todayDay();
  const { rangeStart, totalDays } = useMemo(() => {
    if (scheduled.length === 0) return { rangeStart: today - 7, totalDays: MIN_DAYS };
    let min = Math.min(...scheduled.map((s) => s.start));
    let max = Math.max(...scheduled.map((s) => s.end));
    // L'oggi rientra nel grafico solo se non è lontanissimo dalle attività.
    if (today >= min - 45 && today <= max + 45) {
      min = Math.min(min, today);
      max = Math.max(max, today);
    }
    min -= 3;
    max += 10;
    if (max - min + 1 < MIN_DAYS) max = min + MIN_DAYS - 1;
    return { rangeStart: min, totalDays: max - min + 1 };
  }, [scheduled, today]);

  // All'apertura si porta in vista l'oggi (o la prima barra).
  useEffect(() => {
    if (!scrollRef.current) return;
    const focusDay = today >= rangeStart && today < rangeStart + totalDays ? today : (scheduled[0]?.start ?? rangeStart);
    scrollRef.current.scrollLeft = Math.max(0, (focusDay - rangeStart) * dayWidth - 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, startKey, endKey]);

  const barColor = (task) => {
    if (statusColumn) {
      const option = statusColumn.options.find((o) => o.value === task.data[String(statusColumn.id)]);
      if (option) return NT_COLORS[option.color] || NT_COLORS.grey;
    }
    return color;
  };

  // Intestazioni: mesi (con numero di giorni) e giorni.
  const months = [];
  const days = [];
  for (let i = 0; i < totalDays; i += 1) {
    const day = rangeStart + i;
    const date = new Date(day * DAY_MS);
    const label = date.toLocaleDateString(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' });
    const last = months[months.length - 1];
    if (last && last.label === label) last.days += 1;
    else months.push({ label, short: date.toLocaleDateString(lang, { month: 'short', timeZone: 'UTC' }), days: 1 });
    days.push({ day, dom: date.getUTCDate(), dow: date.getUTCDay() });
  }

  const startDrag = (e, item, mode) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { id: item.task.id, mode, originX: e.clientX, start: item.start, end: item.end, moved: false };
    const onMove = (ev) => {
      const state = dragRef.current;
      const delta = Math.round((ev.clientX - state.originX) / dayWidth);
      let { start, end } = state;
      if (mode === 'move') {
        start += delta;
        end += delta;
      } else if (mode === 'start') {
        start = Math.min(start + delta, end);
      } else {
        end = Math.max(end + delta, start);
      }
      state.moved = state.moved || delta !== 0;
      state.next = { start, end };
      setDrag({ id: state.id, start, end });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const state = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!state.moved) {
        onOpen(item.task);
        return;
      }
      onShift(item.task, toIso(state.next.start), toIso(state.next.end), mode !== 'move' || item.hasEnd);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Frecce di dipendenza: dalla fine di ogni attività "precedente" all'inizio di quella che ne dipende.
  const dependencyLines = [];
  if (dependencyColumn) {
    const byId = new Map(scheduled.map((item, i) => [item.task.id, { item, row: i }]));
    const span = (item) => (drag && drag.id === item.task.id ? { start: drag.start, end: drag.end } : { start: item.start, end: item.end });
    scheduled.forEach((item, row) => {
      const predecessors = item.task.data[String(dependencyColumn.id)];
      (Array.isArray(predecessors) ? predecessors : []).forEach((id) => {
        const from = byId.get(id);
        if (!from) return;
        const a = span(from.item);
        const b = span(item);
        const x1 = (a.end - rangeStart + 1) * dayWidth;
        const y1 = from.row * ROW_HEIGHT + ROW_HEIGHT / 2;
        const x2 = (b.start - rangeStart) * dayWidth;
        const y2 = row * ROW_HEIGHT + ROW_HEIGHT / 2;
        const d = x2 >= x1 + 12
          ? `M${x1},${y1} H${x1 + 8} V${y2} H${x2}`
          : `M${x1},${y1} H${x1 + 8} V${(y1 + y2) / 2} H${x2 - 8} V${y2} H${x2}`;
        dependencyLines.push({ d, late: b.start <= a.end });
      });
    });
  }

  const todayOffset = (today - rangeStart) * dayWidth;
  const resizable = Boolean(endColumn) || isRange;

  return (
    <div className="gantt">
      <div className="gantt-left">
        <div className="gantt-corner">{t('projectDetail.ganttTasks')}</div>
        {scheduled.map(({ task }) => (
          <button key={task.id} type="button" className="gantt-row-label" onClick={() => onOpen(task)}>
            {task.title || t('projectDetail.untitled')}
          </button>
        ))}
      </div>
      <div className="gantt-scroll" ref={scrollRef}>
        <div className="gantt-inner" style={{ width: totalDays * dayWidth, '--dw': `${dayWidth}px` }}>
          <div className="gantt-head">
            <div className="gantt-months">
              {months.map((m, i) => (
                <span key={i} className="gantt-month" style={{ width: m.days * dayWidth }}>
                  <span className="gantt-month-label">{m.days * dayWidth < 28 ? '' : (m.days * dayWidth < 90 ? m.short : m.label)}</span>
                </span>
              ))}
            </div>
            <div className="gantt-days">
              {days.map((d) => (
                <span
                  key={d.day}
                  className={'gantt-day' + (d.dow === 0 || d.dow === 6 ? ' gantt-day-weekend' : '') + (d.day === today ? ' gantt-day-today' : '')}
                  style={{ width: dayWidth }}
                >
                  {(zoom === 'day' || (zoom === 'week' && d.dow === 1)) ? d.dom : ''}
                </span>
              ))}
            </div>
          </div>
          <div className="gantt-body" style={zoom === 'day' ? undefined : { backgroundImage: 'none' }}>
            {dependencyColumn && scheduled.length > 0 && (
              <svg className="gantt-deps" width={totalDays * dayWidth} height={scheduled.length * ROW_HEIGHT} aria-hidden="true">
                <defs>
                  <marker id="gantt-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L8,4 L0,8 z" fill="currentColor" />
                  </marker>
                </defs>
                {dependencyLines.map((line, i) => (
                  <path key={i} d={line.d} className={'gantt-dep' + (line.late ? ' gantt-dep-late' : '')} markerEnd="url(#gantt-arrow)" />
                ))}
              </svg>
            )}
            {today >= rangeStart && today < rangeStart + totalDays && <span className="gantt-today" style={{ left: todayOffset + dayWidth / 2 }} />}
            {scheduled.map((item) => {
              const preview = drag && drag.id === item.task.id ? drag : null;
              const start = preview ? preview.start : item.start;
              const end = preview ? preview.end : item.end;
              return (
                <div key={item.task.id} className="gantt-row">
                  <div
                    className={'gantt-bar' + (preview ? ' gantt-bar-dragging' : '')}
                    style={{ left: (start - rangeStart) * dayWidth, width: (end - start + 1) * dayWidth, '--bar': barColor(item.task) }}
                    onMouseDown={(e) => startDrag(e, item, 'move')}
                    title={item.task.title}
                  >
                    {resizable && <span className="gantt-handle gantt-handle-start" onMouseDown={(e) => startDrag(e, item, 'start')} />}
                    <span className="gantt-bar-label">{item.task.title || t('projectDetail.untitled')}</span>
                    {resizable && <span className="gantt-handle gantt-handle-end" onMouseDown={(e) => startDrag(e, item, 'end')} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {scheduled.length === 0 && <p className="nt-empty gantt-empty">{t('projectDetail.ganttEmpty')}</p>}
      {unscheduled.length > 0 && (
        <div className="gantt-unscheduled">
          <div className="nt-muted gantt-unscheduled-title">{t('projectDetail.ganttUnscheduled', { count: unscheduled.length })}</div>
          {unscheduled.map((task) => (
            <button key={task.id} type="button" className="nt-ghost-btn" onClick={() => onOpen(task)}>
              {task.title || t('projectDetail.untitled')}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProjectGantt;
